import React from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShieldHalved,
    faFileAlt,
    faRotateRight,
} from '@fortawesome/free-solid-svg-icons';
import { DecisionGuide, DrawerDecisionGuideFetchStatus } from '../DrawerFacetTypes';
import {
    DECISION_GUIDE_DRAWER_ERROR_BODY,
    DECISION_GUIDE_DRAWER_ERROR_TITLE,
    DECISION_GUIDE_DRAWER_LOADING_BODY,
    DECISION_GUIDE_DRAWER_LOADING_TITLE,
    DECISION_GUIDE_DRAWER_MALFORMED_BODY,
    DECISION_GUIDE_DRAWER_MALFORMED_TITLE,
} from './decisionGuideCopy';
import { isRenderableDecisionGuide } from './isRenderableDecisionGuide';

export interface DrawerDecisionGuideSectionProps {
    status: DrawerDecisionGuideFetchStatus;
    decisionGuide: DecisionGuide | null;
    onCopy: (value: string) => void;
    onRetry?: () => void;
    readOnly?: boolean;
}

/**
 * Workbench drawer block: explicit loading / ready / error for get_document-backed guidance.
 * Trusts server-sanitized payloads; hides pathways when shape is unexpected.
 */
const DrawerDecisionGuideSection: React.FC<DrawerDecisionGuideSectionProps> = ({
    status,
    decisionGuide,
    onCopy,
    onRetry,
    readOnly = false,
}) => {
    if (status === 'inactive') {
        return null;
    }

    if (status === 'loading') {
        return (
            <Box
                sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: 'rgba(var(--color-accent-rgb), 0.04)',
                    border: '1px solid var(--color-accent)',
                    display: 'grid',
                    gap: 1.5,
                }}
                role="status"
                aria-live="polite"
                aria-busy="true"
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CircularProgress size={22} sx={{ color: 'var(--color-accent)' }} aria-hidden />
                    <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 700, color: 'var(--color-text-primary)' }}>
                        {DECISION_GUIDE_DRAWER_LOADING_TITLE}
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)' }}>
                    {DECISION_GUIDE_DRAWER_LOADING_BODY}
                </Typography>
            </Box>
        );
    }

    if (status === 'error') {
        return (
            <Box
                sx={{
                    p: 2,
                    borderRadius: 3,
                    border: '1px solid var(--color-border-hover)',
                    bgcolor: 'var(--color-bg-card)',
                    display: 'grid',
                    gap: 1,
                }}
                role="alert"
            >
                <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 700, color: 'var(--color-text-primary)' }}>
                    {DECISION_GUIDE_DRAWER_ERROR_TITLE}
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)' }}>
                    {DECISION_GUIDE_DRAWER_ERROR_BODY}
                </Typography>
                {onRetry && (
                    <Button
                        size="small"
                        variant="text"
                        onClick={onRetry}
                        sx={{ alignSelf: 'flex-start', textTransform: 'none', px: 0 }}
                        startIcon={<FontAwesomeIcon icon={faRotateRight} style={{ fontSize: '0.75rem' }} />}
                    >
                        Retry
                    </Button>
                )}
            </Box>
        );
    }

    if (decisionGuide != null && !isRenderableDecisionGuide(decisionGuide)) {
        return (
            <Box
                sx={{
                    p: 2,
                    borderRadius: 3,
                    border: '1px solid var(--color-border-hover)',
                    bgcolor: 'var(--color-bg-card)',
                    display: 'grid',
                    gap: 1,
                }}
                role="alert"
            >
                <Typography variant="subtitle2" component="h3" sx={{ fontWeight: 700, color: 'var(--color-text-primary)' }}>
                    {DECISION_GUIDE_DRAWER_MALFORMED_TITLE}
                </Typography>
                <Typography variant="body2" sx={{ color: 'var(--color-text-secondary)' }}>
                    {DECISION_GUIDE_DRAWER_MALFORMED_BODY}
                </Typography>
            </Box>
        );
    }

    if (!isRenderableDecisionGuide(decisionGuide)) {
        return null;
    }

    const g = decisionGuide;
    const c = g.templates?.call_script;
    const l = g.templates?.letter_template;
    const responseTemplateText = (typeof c === 'string' && c.length > 0)
        ? c
        : (typeof l === 'string' && l.length > 0 ? l : null);

    return (
        <Box
            component="section"
            aria-labelledby="decision-guide-heading"
            sx={{ p: 2, borderRadius: 3, bgcolor: 'rgba(var(--color-accent-rgb), 0.04)', border: '1px solid var(--color-accent)', display: 'grid', gap: 2 }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <FontAwesomeIcon icon={faShieldHalved} style={{ fontSize: '1.2rem', color: 'var(--color-accent)' }} aria-hidden />
                <Typography id="decision-guide-heading" variant="subtitle2" component="h2" sx={{ fontWeight: 700, color: 'var(--color-text-primary)' }}>
                    {readOnly ? 'Document summary' : 'Your options'}
                </Typography>
            </Box>

            {/* Our reading of the document, not a quotation from it. */}
            <Typography variant="body2" sx={{ color: 'var(--color-text-primary)', pl: 1, borderLeft: '3px solid var(--color-accent)' }}>
                {g.calm_summary}
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box component="section" aria-labelledby="known-facts-heading">
                    <Typography id="known-facts-heading" variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'var(--color-text-muted)', mb: 1, display: 'block' }}>
                        Known facts
                    </Typography>
                    <Box sx={{ display: 'grid', gap: 1 }}>
                        {g.facts.map((f, i) => (
                            <Box key={i} sx={{ p: 1, borderRadius: 1.5, bgcolor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)' }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                                    {f.label}: {f.value}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', fontSize: '0.65rem' }}>
                                    Source: {f.evidence}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
                <Box component="section" aria-labelledby="missing-info-heading">
                    <Typography id="missing-info-heading" variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'var(--color-text-muted)', mb: 1, display: 'block' }}>
                        Missing info
                    </Typography>
                    <Box sx={{ display: 'grid', gap: 1 }}>
                        {g.unknowns.map((u, i) => (
                            <Box key={i} sx={{ p: 1, borderRadius: 1.5, bgcolor: 'var(--color-bg-card-hover)', border: '1px solid var(--color-border)' }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'var(--color-text-primary)', display: 'block' }}>
                                    Needs clarification: {u.label}
                                </Typography>
                                {u.why && (
                                    <Typography variant="caption" sx={{ color: 'var(--color-text-secondary)', fontSize: '0.65rem' }}>
                                        {u.why}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>

            {!readOnly && <>
            <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'var(--color-bg-primary)', border: '1px solid var(--color-accent)' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'var(--color-accent-text)', display: 'block', mb: 0.5 }}>
                    Safe first step
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {g.safe_first_step}
                </Typography>
            </Box>

            <Box component="section" aria-labelledby="pathways-heading">
                <Typography id="pathways-heading" variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'var(--color-text-muted)', mb: 1, display: 'block' }}>
                    Paths
                </Typography>
                <Box component="ul" sx={{ display: 'grid', gap: 1.5, listStyle: 'none', m: 0, p: 0 }}>
                    {g.pathways.map((p, pathwayIndex) => (
                        <Box
                            component="li"
                            key={`pathway-${pathwayIndex}`}
                            sx={{ p: 1.5, borderRadius: 2, bgcolor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)' }}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
                                <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    Pathway option {pathwayIndex + 1} of {g.pathways.length}
                                </Typography>
                                <Typography variant="body2" component="h3" sx={{ fontWeight: 700, color: 'var(--color-text-primary)' }}>
                                    {p.label}
                                </Typography>
                            </Box>
                            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'var(--color-text-primary)' }}>
                                <strong>Outcome:</strong> {p.outcome}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'var(--color-text-primary)' }}>
                                <strong>Avoid:</strong> {p.avoid}
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: 'var(--color-text-muted)' }}>
                                Steps
                            </Typography>
                            <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                                {p.steps.map((s, si) => (
                                    <Typography key={si} component="li" variant="caption" sx={{ color: 'var(--color-text-muted)', display: 'list-item' }}>
                                        {s}
                                    </Typography>
                                ))}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>

            {responseTemplateText && (
                <Box sx={{ mt: 1 }}>
                    <Button
                        size="small"
                        variant="text"
                        startIcon={<FontAwesomeIcon icon={faFileAlt} />}
                        sx={{ color: 'var(--color-accent-text)', fontSize: '0.75rem' }}
                        onClick={() => onCopy(responseTemplateText)}
                    >
                        Copy response template
                    </Button>
                </Box>
            )}

            </>}
            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', fontSize: '0.6rem', textAlign: 'center' }}>
                {g.disclaimer}
            </Typography>
        </Box>
    );
};

export default DrawerDecisionGuideSection;
